import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/app/StatusPill';
import { formatINR, fmtDate, periodLabel } from '@/lib/format';
import { Building2, MapPin, Receipt, Wrench, Bell, ArrowRight, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantDashboard() {
  const [data, setData] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: 0, mode: 'upi', reference: '', payment_date: new Date().toISOString().slice(0, 10), note: '' });

  const load = () => api.get('/dashboard/tenant').then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const startPay = () => {
    if (!data?.current_demand) return;
    setPay({ amount: data.current_demand.outstanding, mode: 'upi', reference: '', payment_date: new Date().toISOString().slice(0, 10), note: '' });
    setPayOpen(true);
  };
  const submitPay = async () => {
    try {
      await api.post('/payments', { demand_id: data.current_demand.id, ...pay, amount: Number(pay.amount) });
      toast.success('Payment recorded — receipt available in Rent & Payments');
      setPayOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const lease = data.lease;
  const cd = data.current_demand;

  return (
    <div>
      <PageHeader title={`Hi, ${(JSON.parse(localStorage.getItem('pmcrm_user') || '{}').name || '').split(' ')[0]} 👋`} subtitle="Your home, payments and requests — in one place" />

      {/* Hero rent due card */}
      <div className="rounded-2xl p-1 hero-bg-tenant">
        <Card className="p-5 md:p-6 border-0 shadow-md bg-card/95 backdrop-blur">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Current rent due</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="text-4xl md:text-5xl font-display font-semibold tabular-nums" data-testid="tenant-rent-due-amount">
                  {formatINR(cd?.outstanding ?? 0)}
                </div>
                {cd && <StatusPill status={cd.status} />}
              </div>
              <div className="text-sm text-muted-foreground mt-1" data-testid="tenant-rent-due-date">
                {cd ? <>Period {periodLabel(cd.period)} · due {fmtDate(cd.due_date)}</> : 'No active demand for this month'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="lg" onClick={startPay} disabled={!cd || cd.outstanding <= 0} className="bg-[hsl(var(--role-accent))] hover:bg-[hsl(var(--role-accent))]/90 text-white" data-testid="tenant-rent-pay-button">
                <IndianRupee className="h-4 w-4 mr-1" /> Pay now
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Lease + payment history */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mt-6">
        <Card className="lg:col-span-5 p-5">
          <div className="flex items-center gap-2 mb-3"><Building2 className="h-4 w-4 text-muted-foreground" /><div className="text-sm font-semibold">My residence</div></div>
          {lease ? (
            <div className="space-y-2 text-sm">
              <div className="text-lg font-semibold">{lease.property_name}</div>
              <div className="text-xs text-muted-foreground flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5" /> {lease.property_address}</div>
              <div className="flex items-center justify-between border-t pt-2 mt-2"><span className="text-muted-foreground text-xs">Unit</span><span className="font-mono">{lease.unit_number}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground text-xs">Agreement</span><span className="font-mono">{lease.agreement_number}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground text-xs">Lease ends</span><span>{fmtDate(lease.end_date)}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground text-xs">Monthly rent</span><span className="tabular-nums font-medium">{formatINR(lease.rent_amount)}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground text-xs">Security deposit</span><span className="tabular-nums">{formatINR(lease.deposit_amount)}</span></div>
            </div>
          ) : <div className="text-sm text-muted-foreground">No active lease.</div>}
        </Card>

        <Card className="lg:col-span-7 p-5">
          <div className="flex items-center gap-2 mb-3"><Receipt className="h-4 w-4 text-muted-foreground" /><div className="text-sm font-semibold">Recent payments</div></div>
          <div className="divide-y">
            {data.recent_payments?.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="font-mono text-xs">{p.receipt_number}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(p.payment_date)} · <span className="uppercase">{p.mode}</span></div>
                </div>
                <div className="tabular-nums font-medium">{formatINR(p.amount)}</div>
              </div>
            ))}
            {!data.recent_payments?.length && <div className="py-6 text-center text-muted-foreground text-sm">No payments yet.</div>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mt-6">
        <Card className="lg:col-span-7 p-5">
          <div className="flex items-center gap-2 mb-3"><Wrench className="h-4 w-4 text-muted-foreground" /><div className="text-sm font-semibold">Open maintenance requests</div></div>
          <div className="space-y-2">
            {data.open_tickets?.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                <div>
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground">{t.ticket_number} · {fmtDate(t.created_at)}</div>
                </div>
                <StatusPill status={t.status} />
              </div>
            ))}
            {!data.open_tickets?.length && <div className="py-6 text-center text-muted-foreground text-sm">No open requests.</div>}
          </div>
        </Card>
        <Card className="lg:col-span-5 p-5">
          <div className="flex items-center gap-2 mb-3"><Bell className="h-4 w-4 text-muted-foreground" /><div className="text-sm font-semibold">Notices</div></div>
          <div className="space-y-2">
            {data.notices?.slice(0, 4).map((n) => (
              <div key={n.id} className="rounded-lg border p-3 text-sm">
                <div className="font-medium">{n.title}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(n.created_at)} · {n.category}</div>
              </div>
            ))}
            {!data.notices?.length && <div className="py-6 text-center text-muted-foreground text-sm">No notices.</div>}
          </div>
        </Card>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pay rent</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <div className="text-muted-foreground">{cd && <>Period {periodLabel(cd.period)} · outstanding {formatINR(cd.outstanding)}</>}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
              <div><Label>Date</Label><Input type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} /></div>
              <div><Label>Mode</Label><Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent></Select></div>
              <div><Label>Reference</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="UTR / Txn ID" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={submitPay} data-testid="tenant-pay-submit">Submit payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
